// School Consent (On-Site School Services) — therapist-side modals.
// Modeled on ROIComponents.jsx: a two-phase send modal (generate → compose +
// send) and a read-only view modal with Print / Send as PDF.

import React, { useState } from 'react';
import API from '../services/api';
import { formatDate } from '../dateUtils';
import {
  SCHOOL_CONSENT_TITLE,
  DEFAULT_SCHOOL_NAME,
  HEADER_LABELS,
  SHARE_OPTIONS,
  SHARE_OTHER_LABEL,
  withSchool,
  bulletLine,
} from '../data/schoolConsentText';

const DEFAULT_EMAIL_MESSAGE =
  'Please complete the consent form for on-site school counseling services by clicking the button below.';

const EMPTY_PREFILL = {
  gradeTeacher: '',
  releaseAuthorized: null,
  releasePersonnel: '',
  shareAttendance: false,
  shareProgressGoals: false,
  shareClassroomStrategies: false,
  shareOther: false,
  shareOtherSpecify: '',
  releaseExpiration: '',
  allowClientEdits: true,
};

// client_name is stored "Last, First" — derive the friendly first name and a "First Last" display.
function parseClientName(name) {
  const raw = (name || '').trim();
  if (!raw) return { firstName: '', fullName: '' };
  if (raw.includes(',')) {
    const [last, first] = raw.split(',').map(s => s.trim());
    return {
      firstName: (first || '').split(/\s+/)[0] || '',
      fullName: [first, last].filter(Boolean).join(' ').trim(),
    };
  }
  const parts = raw.split(/\s+/);
  return { firstName: parts[0] || '', fullName: raw };
}

// Submission rows carry `data` as JSON (already parsed by the API, but be tolerant).
export function submissionData(submission) {
  if (!submission) return {};
  const d = submission.data;
  if (typeof d === 'string') {
    try { return JSON.parse(d); } catch { return {}; }
  }
  return d || {};
}

function esc(s) {
  return String(s == null ? '' : s)
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

function isDataImage(v) {
  return typeof v === 'string' && v.startsWith('data:image');
}

// ── Print HTML (shared by the public page's "Print Copy" and the view modal) ──
export function buildSchoolConsentPrintHtml(data, { schoolName, submittedAt } = {}) {
  const school = schoolName || data.schoolName || DEFAULT_SCHOOL_NAME;
  const t = withSchool(school);
  const origin = (typeof window !== 'undefined' && window.location?.origin) || 'https://avenuesportal.com';
  const box = (on) => `<span class="box">${on ? '&#10003;' : '&nbsp;'}</span>`;
  const sig = (dataUrl) => (isDataImage(dataUrl)
    ? `<img class="sig" src="${dataUrl}" alt="Signature">`
    : '<span class="sig-missing">Not signed</span>');
  const yes = data.releaseAuthorized === true;
  const no = data.releaseAuthorized === false;

  return `<!DOCTYPE html>
<html><head><meta charset="utf-8"><title>${esc(SCHOOL_CONSENT_TITLE)}</title>
<style>
  @page { size: letter; margin: 0.6in; }
  body { font-family: 'Times New Roman', Times, serif; font-size: 11.5px; line-height: 1.45; color: #000; margin: 0; padding: 20px; }
  .page { max-width: 760px; margin: 0 auto; }
  .letterhead { text-align: center; margin-bottom: 18px; }
  .letterhead img { width: 260px; max-width: 60%; height: auto; }
  .footer { text-align: center; margin-top: 28px; }
  .footer img { max-width: 100%; height: auto; }
  h1 { font-size: 15px; text-align: center; margin: 0 0 14px; }
  h2 { font-size: 12.5px; margin: 14px 0 5px; page-break-after: avoid; }
  p { margin: 0 0 6px; }
  ul { margin: 0 0 6px 20px; padding: 0; }
  li { margin: 0 0 3px; }
  .hdr { display: grid; grid-template-columns: 1fr 1fr; gap: 3px 18px; margin-bottom: 8px; }
  .hdr div { border-bottom: 1px solid #999; padding-bottom: 2px; }
  .lbl { font-weight: bold; }
  .box { display: inline-block; width: 11px; height: 11px; border: 1px solid #000; text-align: center; line-height: 11px; font-size: 10px; margin-right: 5px; vertical-align: -1px; }
  .sigblock { margin: 8px 0 4px; page-break-inside: avoid; }
  .sig { display: block; max-width: 240px; max-height: 60px; border-bottom: 1px solid #000; margin: 2px 0 4px; }
  .sig-missing { display: inline-block; color: #777; font-style: italic; }
  .field { margin: 3px 0; }
  .indent { margin-left: 18px; }
  .section { page-break-inside: avoid; }
</style></head>
<body><div class="page">
  <div class="letterhead"><img src="${origin}/letterhead.png" alt="Avenues Unlimited Counseling Center"></div>
  <h1>${esc(SCHOOL_CONSENT_TITLE)}</h1>

  <div class="hdr">
    <div><span class="lbl">${esc(HEADER_LABELS.clientName)}:</span> ${esc(data.clientName)}</div>
    <div><span class="lbl">${esc(HEADER_LABELS.clientDob)}:</span> ${esc(formatDate(data.clientDob))}</div>
    <div><span class="lbl">${esc(HEADER_LABELS.schoolName)}:</span> ${esc(school)}</div>
    <div><span class="lbl">${esc(HEADER_LABELS.gradeTeacher)}:</span> ${esc(data.gradeTeacher)}</div>
  </div>

  <h2>${esc(t.purpose.title)}</h2>
  <p>${esc(t.purpose.body)}</p>

  <h2>${esc(t.services.title)}</h2>
  <ul>${t.services.bullets.map(b => `<li><b>${esc(b.lead)}:</b> ${esc(b.text)}</li>`).join('')}</ul>

  <h2>${esc(t.confidentiality.title)}</h2>
  <p>${esc(t.confidentiality.intro)}</p>
  <ul>${t.confidentiality.bullets.map(b => `<li>${esc(b)}</li>`).join('')}</ul>

  <div class="section">
  <h2>${esc(t.consent.title)}</h2>
  <p>${esc(t.consent.intro)}</p>
  <ul>${t.consent.bullets.map(b => `<li>${esc(b)}</li>`).join('')}</ul>
  </div>

  <div class="section">
  <h2>${esc(t.release.title)}</h2>
  <p><i>${esc(t.release.checkOne)}</i></p>
  <p>${box(yes)}<b>${esc(t.release.yesLead)}</b> &ndash; ${esc(t.release.yesText)}</p>
  <div class="indent">
    <div class="field"><span class="lbl">${esc(t.release.personnelLabel)}:</span> ${esc(yes ? data.releasePersonnel : '')}</div>
    <div class="field lbl">${esc(t.release.sharedHeading)}</div>
    ${t.release.shareOptions.map(o => `<div class="field">${box(yes && !!data[o.key])}${esc(o.label)}</div>`).join('')}
    <div class="field">${box(yes && !!data.shareOther)}${esc(t.release.otherLabel)}: ${esc(yes ? data.shareOtherSpecify : '')}</div>
    <div class="field">${esc(t.release.expirationLead)} <u>&nbsp;${esc(yes ? data.releaseExpiration : '')}&nbsp;</u> ${esc(t.release.expirationHint)}. ${esc(t.release.expirationTail)}</div>
  </div>
  <p>${box(no)}<b>${esc(t.release.noLead)}</b> &ndash; ${esc(t.release.noText)}</p>
  </div>

  <div class="section">
  <h2>${esc(t.acknowledgment.title)}</h2>
  <p>${esc(t.acknowledgment.body)}</p>
  <div class="sigblock">
    <div class="lbl">${esc(t.acknowledgment.signatureLabel)}:</div>
    ${sig(data.ackSignatureData)}
    <div class="field"><span class="lbl">${esc(t.acknowledgment.printedNameLabel)}:</span> ${esc(data.ackPrintedName)}</div>
    <div class="field"><span class="lbl">${esc(t.acknowledgment.dateLabel)}:</span> ${esc(formatDate(data.ackSignatureDate))}</div>
  </div>
  </div>

  ${submittedAt ? `<p style="margin-top:14px;color:#555;font-size:10px;">Submitted electronically ${esc(new Date(submittedAt).toLocaleString())}</p>` : ''}
  <div class="footer"><img src="${origin}/footer.png" alt=""></div>
</div>
<script>window.onload = function () { window.focus(); window.print(); };</script>
</body></html>`;
}

export function openPrintWindow(html) {
  const w = window.open('', '_blank', 'width=850,height=950');
  if (!w) {
    alert('Failed to open new window for printing. Please check your popup blocker settings.');
    return;
  }
  w.document.write(html);
  w.document.close();
}

// ── Send modal ────────────────────────────────────────────────────────────────
export function SendSchoolConsentModal({ isOpen, onClose, clientId, clientName, clientEmail }) {
  const { firstName: clientFirstName, fullName: clientFullName } = parseClientName(clientName);

  const [schoolName, setSchoolName] = useState(DEFAULT_SCHOOL_NAME);
  // Optional prefill (like the ROI modal's preFilledData). Empty values are
  // left for the parent to fill; releaseAuthorized null = parent chooses.
  const [prefill, setPrefill] = useState(EMPTY_PREFILL);
  const [token, setToken] = useState('');
  const [generatedLink, setGeneratedLink] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [linkCopied, setLinkCopied] = useState(false);

  const [emailTo, setEmailTo] = useState('');
  const [greetingName, setGreetingName] = useState('');
  const [emailMessage, setEmailMessage] = useState('');
  const [emailSending, setEmailSending] = useState(false);
  const [emailStatus, setEmailStatus] = useState('');

  const setPf = (field, value) => setPrefill(prev => ({ ...prev, [field]: value }));

  const resetForm = () => {
    setSchoolName(DEFAULT_SCHOOL_NAME);
    setPrefill(EMPTY_PREFILL);
    setToken('');
    setGeneratedLink('');
    setError('');
    setLinkCopied(false);
    setEmailTo('');
    setGreetingName('');
    setEmailMessage('');
    setEmailStatus('');
  };

  const handleClose = () => { resetForm(); onClose(); };

  const generateLink = async () => {
    if (!clientId) { setError('No client selected'); return; }
    if (!schoolName.trim()) { setError('Please enter the school name'); return; }
    setLoading(true);
    setError('');
    try {
      // Only send prefill values that were actually entered.
      const preFilledData = { allowClientEdits: prefill.allowClientEdits };
      if (prefill.gradeTeacher.trim()) preFilledData.gradeTeacher = prefill.gradeTeacher.trim();
      if (prefill.releaseAuthorized !== null) preFilledData.releaseAuthorized = prefill.releaseAuthorized;
      if (prefill.releaseAuthorized === true) {
        if (prefill.releasePersonnel.trim()) preFilledData.releasePersonnel = prefill.releasePersonnel.trim();
        SHARE_OPTIONS.forEach(o => { if (prefill[o.key]) preFilledData[o.key] = true; });
        if (prefill.shareOther) preFilledData.shareOther = true;
        if (prefill.shareOther && prefill.shareOtherSpecify.trim()) preFilledData.shareOtherSpecify = prefill.shareOtherSpecify.trim();
        if (prefill.releaseExpiration.trim()) preFilledData.releaseExpiration = prefill.releaseExpiration.trim();
      }

      const response = await API.post('/forms/school-consent/generate', {
        clientId,
        schoolName: schoolName.trim(),
        preFilledData,
      });
      if (response.data?.link && response.data?.token) {
        setToken(response.data.token);
        setGeneratedLink(response.data.link);
        setEmailTo(clientEmail || '');
        setGreetingName(clientFirstName);
        setEmailMessage(DEFAULT_EMAIL_MESSAGE);
        setEmailStatus('');
      } else {
        setError(response.data?.error || 'Failed to generate link');
      }
    } catch (err) {
      console.error('Error generating school consent link:', err);
      setError(err.response?.data?.error || 'Server error generating link');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = () => {
    if (!generatedLink) return;
    navigator.clipboard.writeText(generatedLink)
      .then(() => { setLinkCopied(true); setTimeout(() => setLinkCopied(false), 3000); })
      .catch(err => console.error('Failed to copy: ', err));
  };

  const handleSendEmail = async () => {
    if (!emailTo) return;
    setEmailSending(true);
    setEmailStatus('');
    try {
      await API.post('/therapist/send-school-consent-link', {
        token,
        recipientEmail: emailTo,
        clientName: greetingName,
        message: emailMessage,
      });
      setEmailStatus('success');
    } catch (err) {
      console.error('Error sending school consent email:', err);
      setEmailStatus('error');
    } finally {
      setEmailSending(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div style={modalStyles.backdrop}>
      <div style={modalStyles.modal}>
        <div style={modalStyles.header}>
          <h3 style={{ margin: 0 }}>Send School Consent Form for {clientName}</h3>
          <button style={modalStyles.closeButton} onClick={handleClose}>×</button>
        </div>

        <div style={modalStyles.body}>
          {!generatedLink ? (
            <>
              <p style={{ marginTop: 0 }}>
                The parent/guardian will receive a link to read, complete and sign the
                <em> Consent to Treatment &amp; Release of Information (On-Site School Services)</em> form.
                The school name below fills the School Name field and every mention of the school in the form text.
              </p>
              <div style={modalStyles.formGroup}>
                <label style={modalStyles.label}>School name</label>
                <input
                  type="text"
                  value={schoolName}
                  onChange={e => setSchoolName(e.target.value)}
                  style={modalStyles.input}
                  placeholder={DEFAULT_SCHOOL_NAME}
                />
              </div>

              <div style={{ border: '1px solid var(--border-color)', borderRadius: 6, padding: '12px 14px', marginBottom: 12, background: 'var(--surface-muted)' }}>
                <div style={{ fontWeight: 600, marginBottom: 4 }}>Prefill (optional)</div>
                <p style={{ margin: '0 0 10px', fontSize: 13, color: 'var(--text-muted)' }}>
                  Anything you fill in here appears on the parent's form already completed. Leave blank to let the parent fill it in.
                  The parent always signs and dates the form themselves.
                </p>

                <div style={modalStyles.formGroup}>
                  <label style={modalStyles.label}>Grade / Teacher</label>
                  <input
                    type="text"
                    value={prefill.gradeTeacher}
                    onChange={e => setPf('gradeTeacher', e.target.value)}
                    style={modalStyles.input}
                    placeholder="e.g., 3rd grade / Mrs. Smith"
                  />
                </div>

                <div style={modalStyles.formGroup}>
                  <label style={modalStyles.label}>Section 5 — Release of information to school personnel</label>
                  <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', fontSize: 14 }}>
                    <label style={{ cursor: 'pointer' }}>
                      <input type="radio" name="sc-prefill-release" checked={prefill.releaseAuthorized === null} onChange={() => setPf('releaseAuthorized', null)} /> Parent chooses
                    </label>
                    <label style={{ cursor: 'pointer' }}>
                      <input type="radio" name="sc-prefill-release" checked={prefill.releaseAuthorized === true} onChange={() => setPf('releaseAuthorized', true)} /> Yes
                    </label>
                    <label style={{ cursor: 'pointer' }}>
                      <input type="radio" name="sc-prefill-release" checked={prefill.releaseAuthorized === false} onChange={() => setPf('releaseAuthorized', false)} /> No
                    </label>
                  </div>
                </div>

                {prefill.releaseAuthorized === true && (
                  <div style={{ marginLeft: 12, paddingLeft: 12, borderLeft: '3px solid var(--primary-color)' }}>
                    <div style={modalStyles.formGroup}>
                      <label style={modalStyles.label}>Authorized school personnel</label>
                      <input
                        type="text"
                        value={prefill.releasePersonnel}
                        onChange={e => setPf('releasePersonnel', e.target.value)}
                        style={modalStyles.input}
                        placeholder="Names and/or roles"
                      />
                    </div>
                    <div style={modalStyles.formGroup}>
                      <label style={modalStyles.label}>Information to be shared</label>
                      {SHARE_OPTIONS.map(o => (
                        <label key={o.key} style={{ display: 'block', fontSize: 14, cursor: 'pointer', marginBottom: 4 }}>
                          <input type="checkbox" checked={!!prefill[o.key]} onChange={e => setPf(o.key, e.target.checked)} /> {o.label}
                        </label>
                      ))}
                      <label style={{ display: 'block', fontSize: 14, cursor: 'pointer', marginBottom: 4 }}>
                        <input type="checkbox" checked={prefill.shareOther} onChange={e => setPf('shareOther', e.target.checked)} /> {SHARE_OTHER_LABEL}
                      </label>
                      {prefill.shareOther && (
                        <input
                          type="text"
                          value={prefill.shareOtherSpecify}
                          onChange={e => setPf('shareOtherSpecify', e.target.value)}
                          style={modalStyles.input}
                          placeholder="Describe the other information"
                        />
                      )}
                    </div>
                    <div style={modalStyles.formGroup}>
                      <label style={modalStyles.label}>Authorization expires on (date or event)</label>
                      <input
                        type="text"
                        value={prefill.releaseExpiration}
                        onChange={e => setPf('releaseExpiration', e.target.value)}
                        style={modalStyles.input}
                        placeholder="e.g., end of the school year"
                      />
                    </div>
                  </div>
                )}

                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, cursor: 'pointer', marginTop: 6 }}>
                  <input
                    type="checkbox"
                    checked={prefill.allowClientEdits}
                    onChange={e => setPf('allowClientEdits', e.target.checked)}
                  />
                  Allow the parent to change prefilled values
                </label>
              </div>

              {error && <div style={modalStyles.error}>{error}</div>}

              <div style={modalStyles.actions}>
                <button style={modalStyles.resetButton} onClick={handleClose}>Cancel</button>
                <button style={modalStyles.generateButton} onClick={generateLink} disabled={loading}>
                  {loading ? 'Generating...' : 'Generate Link'}
                </button>
              </div>
            </>
          ) : (
            <div style={{ padding: '8px 0' }}>
              <h4 style={{ color: 'var(--primary-color)', margin: '0 0 4px 0' }}>✓ School Consent Form Link Generated</h4>
              <p style={{ margin: '0 0 12px 0', fontSize: 13, color: 'var(--text-strong)' }}>
                Send the form link to {clientFullName || 'the client'} via email, or copy the link to send it yourself.
                {' '}School: <strong>{schoolName}</strong>.
              </p>

              <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 16 }}>
                <input
                  type="text"
                  readOnly
                  value={generatedLink}
                  onFocus={e => e.target.select()}
                  style={{ flex: 1, minWidth: 0, padding: '8px 10px', border: '1px solid var(--border-color)', borderRadius: 6, fontSize: 13, background: 'var(--surface-muted)', color: 'var(--text-strong)', boxSizing: 'border-box' }}
                />
                <button
                  type="button"
                  onClick={copyToClipboard}
                  style={{ ...modalStyles.resetButton, whiteSpace: 'nowrap', background: linkCopied ? 'var(--primary-color)' : undefined, color: linkCopied ? '#fff' : undefined }}
                >
                  {linkCopied ? '✓ Copied!' : 'Copy Link'}
                </button>
              </div>

              {emailStatus === 'success' ? (
                <div style={{ textAlign: 'center', padding: '24px 0' }}>
                  <div style={{ fontSize: 40 }}>✅</div>
                  <p style={{ color: 'var(--primary-color)', fontWeight: 600, marginTop: 8 }}>School consent form link sent successfully!</p>
                  <button style={{ ...modalStyles.generateButton, marginTop: 16 }} onClick={handleClose}>Done</button>
                </div>
              ) : (
                <div style={{ display: 'flex', gap: 20, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                  {/* Compose panel */}
                  <div style={{ flex: 1, minWidth: 260 }}>
                    <div style={{ marginBottom: 12 }}>
                      <label style={modalStyles.label}>To</label>
                      <input
                        type="email"
                        value={emailTo}
                        onChange={e => setEmailTo(e.target.value)}
                        placeholder="parent@email.com"
                        style={modalStyles.input}
                      />
                    </div>
                    <div style={{ marginBottom: 12 }}>
                      <label style={modalStyles.label}>Recipient name (greeting)</label>
                      <input
                        type="text"
                        value={greetingName}
                        onChange={e => setGreetingName(e.target.value)}
                        placeholder="First Last"
                        style={modalStyles.input}
                      />
                    </div>
                    <div style={{ marginBottom: 12 }}>
                      <label style={modalStyles.label}>Message</label>
                      <textarea
                        value={emailMessage}
                        onChange={e => setEmailMessage(e.target.value)}
                        rows={5}
                        placeholder="Message to the parent/guardian..."
                        style={{ ...modalStyles.input, fontSize: 13, resize: 'vertical' }}
                      />
                    </div>
                    {emailStatus === 'error' && (
                      <p style={{ color: 'var(--error-color)', fontSize: 13, margin: '0 0 8px 0' }}>Failed to send. Please try again.</p>
                    )}
                    <div style={{ display: 'flex', gap: 10 }}>
                      <button
                        style={{ ...modalStyles.generateButton, opacity: emailSending || !emailTo ? 0.6 : 1 }}
                        onClick={handleSendEmail}
                        disabled={emailSending || !emailTo}
                      >
                        {emailSending ? 'Sending…' : 'Send Email'}
                      </button>
                      <button style={modalStyles.resetButton} onClick={handleClose}>Skip</button>
                    </div>
                  </div>

                  {/* Preview panel */}
                  <div style={{ flex: 1, minWidth: 260, border: '1px solid var(--border-color)', borderRadius: 8, overflow: 'hidden', fontSize: 12 }}>
                    <div style={{ background: 'var(--primary-color)', padding: '14px 16px', textAlign: 'center' }}>
                      <span style={{ color: '#fff', fontWeight: 700, fontSize: 14 }}>Avenues Unlimited Counseling Center</span>
                    </div>
                    <div style={{ padding: '16px', background: '#fff' }}>
                      <p style={{ margin: '0 0 10px 0', fontSize: 13 }}>Hello{greetingName ? ` ${greetingName}` : ''},</p>
                      {emailMessage
                        ? <p style={{ margin: '0 0 10px 0', fontSize: 13, whiteSpace: 'pre-wrap' }}>{emailMessage}</p>
                        : <p style={{ margin: '0 0 10px 0', fontSize: 13, color: 'var(--text-muted)' }}>{DEFAULT_EMAIL_MESSAGE}</p>
                      }
                      <div style={{ textAlign: 'center', margin: '16px 0' }}>
                        <span style={{ background: 'var(--primary-color)', color: '#fff', padding: '10px 22px', borderRadius: 6, fontSize: 13, display: 'inline-block' }}>
                          Complete the Consent Form →
                        </span>
                      </div>
                      <p style={{ fontSize: 11, color: 'var(--text-muted)', margin: 0 }}>This link will expire in 7 days.</p>
                    </div>
                    <div style={{ background: 'var(--surface-muted)', padding: '10px', textAlign: 'center', fontSize: 10, color: 'var(--text-muted)' }}>
                      Avenues Unlimited Counseling Center &bull; 1774 Mellwood Ave, Louisville, KY &bull; 502.459.9635
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── View modal ────────────────────────────────────────────────────────────────
export function ViewSchoolConsentModal({ isOpen, onClose, submission, onSendAsPDF }) {
  if (!isOpen || !submission) return null;

  const data = submissionData(submission);
  const school = submission.school_name || data.schoolName || DEFAULT_SCHOOL_NAME;
  const t = withSchool(school);
  const yes = data.releaseAuthorized === true;
  const no = data.releaseAuthorized === false;

  const Check = ({ on, children }) => (
    <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', margin: '3px 0' }}>
      <span style={{ ...viewStyles.box, background: on ? 'var(--primary-color)' : '#fff', color: '#fff' }}>{on ? '✓' : ''}</span>
      <span>{children}</span>
    </div>
  );
  const Sig = ({ src }) => (isDataImage(src)
    ? <img src={src} alt="Signature" style={viewStyles.sigImage} />
    : <span style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>Not signed</span>);

  const handlePrint = () => {
    openPrintWindow(buildSchoolConsentPrintHtml(data, { schoolName: school, submittedAt: submission.created_at }));
  };

  return (
    <div style={modalStyles.backdrop}>
      <div style={{ ...modalStyles.modal, maxHeight: '90vh' }}>
        <div style={modalStyles.header}>
          <h3 style={{ margin: 0 }}>School Consent — {data.clientName || ''}</h3>
          <button style={modalStyles.closeButton} onClick={onClose}>×</button>
        </div>

        <div style={{ ...modalStyles.body, fontSize: 14, lineHeight: 1.5 }}>
          <div style={viewStyles.docTitle}>{SCHOOL_CONSENT_TITLE}</div>

          <div style={viewStyles.infoGrid}>
            <div><strong>{HEADER_LABELS.clientName}:</strong> {data.clientName || '—'}</div>
            <div><strong>{HEADER_LABELS.clientDob}:</strong> {formatDate(data.clientDob) || '—'}</div>
            <div><strong>{HEADER_LABELS.schoolName}:</strong> {school}</div>
            <div><strong>{HEADER_LABELS.gradeTeacher}:</strong> {data.gradeTeacher || '—'}</div>
            <div><strong>Submitted:</strong> {submission.created_at ? new Date(submission.created_at).toLocaleString() : '—'}</div>
          </div>

          <h4 style={viewStyles.h4}>{t.purpose.title}</h4>
          <p style={viewStyles.p}>{t.purpose.body}</p>

          <h4 style={viewStyles.h4}>{t.services.title}</h4>
          <ul style={viewStyles.ul}>
            {t.services.bullets.map(b => <li key={b.lead}><strong>{b.lead}:</strong> {b.text}</li>)}
          </ul>

          <h4 style={viewStyles.h4}>{t.confidentiality.title}</h4>
          <p style={viewStyles.p}>{t.confidentiality.intro}</p>
          <ul style={viewStyles.ul}>{t.confidentiality.bullets.map(b => <li key={b}>{bulletLine(b)}</li>)}</ul>

          <h4 style={viewStyles.h4}>{t.consent.title}</h4>
          <p style={viewStyles.p}>{t.consent.intro}</p>
          <ul style={viewStyles.ul}>{t.consent.bullets.map(b => <li key={b}>{b}</li>)}</ul>

          <h4 style={viewStyles.h4}>{t.release.title}</h4>
          <p style={{ ...viewStyles.p, fontStyle: 'italic' }}>{t.release.checkOne}</p>
          <Check on={yes}><strong>{t.release.yesLead}</strong> – {t.release.yesText}</Check>
          <div style={{ marginLeft: 28 }}>
            <div><strong>{t.release.personnelLabel}:</strong> {yes ? (data.releasePersonnel || '—') : '—'}</div>
            <div style={{ marginTop: 4 }}><strong>{t.release.sharedHeading}</strong></div>
            {t.release.shareOptions.map(o => <Check key={o.key} on={yes && !!data[o.key]}>{o.label}</Check>)}
            <Check on={yes && !!data.shareOther}>{t.release.otherLabel}: {yes ? (data.shareOtherSpecify || '') : ''}</Check>
            <div style={{ marginTop: 4 }}>
              {t.release.expirationLead} <u>{yes ? (data.releaseExpiration || '—') : '—'}</u> {t.release.expirationHint}. {t.release.expirationTail}
            </div>
          </div>
          <div style={{ marginTop: 8 }}>
            <Check on={no}><strong>{t.release.noLead}</strong> – {t.release.noText}</Check>
          </div>

          <h4 style={viewStyles.h4}>{t.acknowledgment.title}</h4>
          <p style={viewStyles.p}>{t.acknowledgment.body}</p>
          <div style={viewStyles.sigBlock}>
            <div><strong>{t.acknowledgment.signatureLabel}:</strong></div>
            <Sig src={data.ackSignatureData} />
            <div><strong>{t.acknowledgment.printedNameLabel}:</strong> {data.ackPrintedName || '—'}</div>
            <div><strong>{t.acknowledgment.dateLabel}:</strong> {formatDate(data.ackSignatureDate) || '—'}</div>
          </div>
        </div>

        <div style={modalStyles.footer}>
          <button style={modalStyles.printButton} onClick={handlePrint}>🖨️ Print Form</button>
          {onSendAsPDF && (
            <button style={modalStyles.printButton} onClick={() => onSendAsPDF(submission)}>✉️ Send as PDF</button>
          )}
          <button style={modalStyles.closeModalButton} onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

// ── styles ────────────────────────────────────────────────────────────────────
const modalStyles = {
  backdrop: {
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex', justifyContent: 'center', alignItems: 'center',
    zIndex: 1000,
  },
  modal: {
    width: '900px', maxWidth: '95vw',
    backgroundColor: 'white', borderRadius: '8px',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
    maxHeight: '85vh', display: 'flex', flexDirection: 'column',
  },
  header: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '20px', borderBottom: '1px solid var(--border-color)',
  },
  closeButton: { background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer', color: 'var(--text-muted)' },
  body: { flex: '1', padding: '20px', overflowY: 'auto' },
  footer: {
    padding: '15px 20px', borderTop: '1px solid var(--border-color)',
    display: 'flex', justifyContent: 'flex-end', gap: '10px',
  },
  formGroup: { marginBottom: '15px' },
  label: { display: 'block', fontWeight: 600, fontSize: 13, marginBottom: 4 },
  input: {
    width: '100%', padding: '8px 10px', border: '1px solid var(--border-color)',
    borderRadius: 6, fontSize: 14, boxSizing: 'border-box',
  },
  actions: { display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '20px' },
  generateButton: {
    backgroundColor: 'var(--primary-color)', color: 'white', border: 'none',
    padding: '10px 20px', borderRadius: '4px', cursor: 'pointer', fontSize: '14px',
  },
  resetButton: {
    backgroundColor: 'var(--text-muted)', color: 'white', border: 'none',
    padding: '10px 20px', borderRadius: '4px', cursor: 'pointer', fontSize: '14px',
  },
  printButton: {
    backgroundColor: 'var(--primary-color)', color: 'white', border: 'none',
    padding: '10px 20px', borderRadius: '4px', cursor: 'pointer', fontSize: '14px',
  },
  closeModalButton: {
    backgroundColor: 'var(--text-muted)', color: 'white', border: 'none',
    padding: '10px 20px', borderRadius: '4px', cursor: 'pointer', fontSize: '14px',
  },
  error: {
    color: 'var(--error-color)', backgroundColor: '#f8d7da', border: '1px solid #f5c6cb',
    borderRadius: '4px', padding: '10px', marginBottom: '15px',
  },
};

const viewStyles = {
  docTitle: {
    textAlign: 'center', fontWeight: 700, fontSize: 16, color: 'var(--primary-color)',
    padding: '10px', border: '2px solid var(--primary-color)', background: 'var(--surface-muted)',
    marginBottom: 16,
  },
  infoGrid: {
    display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
    gap: '6px 16px', marginBottom: 8, fontSize: 14,
  },
  h4: {
    color: 'var(--primary-color)', margin: '18px 0 6px', fontSize: 15,
    borderBottom: '1px solid var(--border-color)', paddingBottom: 4,
  },
  p: { margin: '0 0 8px' },
  ul: { margin: '0 0 8px 22px', padding: 0 },
  box: {
    display: 'inline-flex', width: 16, height: 16, border: '1px solid var(--primary-color)',
    borderRadius: 3, alignItems: 'center', justifyContent: 'center', fontSize: 12,
    flexShrink: 0, marginTop: 3,
  },
  sigBlock: {
    border: '1px solid var(--border-color)', borderRadius: 6, padding: '10px 12px',
    background: 'var(--surface-muted)', margin: '10px 0', display: 'flex', flexDirection: 'column', gap: 4,
  },
  sigImage: { maxWidth: 280, maxHeight: 90, background: '#fff', border: '1px solid var(--border-color)', borderRadius: 4 },
};
